/**
 * MONSTER TOWN — THE DRAWING KIT. FISHING/BUGS CREW (src/catching/**).
 *
 * The handful of primitives every picture this crew draws is built from, split
 * out of `art2d.ts` when that file went past the repo's 500-line rule. Nothing
 * here knows what a fish is.
 *
 * HOUSE STYLE, so twenty pictures look like one hand drew them:
 *  - authored in a 100x100 box, scaled to whatever size is asked for;
 *  - one ink colour, round caps and joins, chunky 5-7 unit strokes;
 *  - fills come from `palette.ts` — no raw hex below except the ink constant,
 *    which is `css('ink')`;
 *  - readable at 96 px on a 1024x768 iPad, which is the real test.
 *
 * ZERO EXTERNAL ASSETS (brief): every picture is a few dozen path calls.
 */

import { css, type PaletteName } from '../core/palette'

export type Painter = (c: CanvasRenderingContext2D, size: number) => void

export const INK = css('ink')

/** Author in a 100x100 box; the caller asks for any size. */
export function inBox(c: CanvasRenderingContext2D, size: number, draw: () => void): void {
  c.save()
  c.scale(size / 100, size / 100)
  c.lineJoin = 'round'
  c.lineCap = 'round'
  c.strokeStyle = INK
  draw()
  c.restore()
}

export function fillStroke(c: CanvasRenderingContext2D, fill: PaletteName, width = 6): void {
  c.fillStyle = css(fill)
  c.fill()
  c.lineWidth = width
  c.strokeStyle = INK
  c.stroke()
}

export function ellipse(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  rot = 0,
): void {
  c.beginPath()
  c.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2)
}

export function dot(c: CanvasRenderingContext2D, x: number, y: number, r: number, fill: PaletteName): void {
  ellipse(c, x, y, r, r)
  c.fillStyle = css(fill)
  c.fill()
}

/** The one eye every creature in this book has. Friendly, never a blank bead. */
export function eye(c: CanvasRenderingContext2D, x: number, y: number, r = 6): void {
  ellipse(c, x, y, r, r)
  c.fillStyle = css('eyeWhite')
  c.fill()
  c.lineWidth = 3
  c.stroke()
  dot(c, x + r * 0.22, y + r * 0.1, r * 0.5, 'pupil')
}

export function line(
  c: CanvasRenderingContext2D,
  pts: readonly (readonly [number, number])[],
  width = 5,
): void {
  c.beginPath()
  const first = pts[0]
  if (first === undefined) return
  c.moveTo(first[0], first[1])
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i]
    if (p !== undefined) c.lineTo(p[0], p[1])
  }
  c.lineWidth = width
  c.strokeStyle = INK
  c.stroke()
}

// ---------------------------------------------------------------------------
// mounting a painter in the DOM
// ---------------------------------------------------------------------------

/**
 * A painter as a HUD element. Deliberately shaped like `iconEl()` — same
 * `.mt-ico` class, same explicit inline size — because the HUD crew's
 * oversized-widget gate measures `.mt-ico` and an unsized picture is the exact
 * defect that gate exists to catch (see `measureOversized` in hud.ts).
 */
export function paintEl(p: Painter, sizePx: number, label = ''): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.style.display = 'block'
  const canvas = document.createElement('canvas')
  const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))
  canvas.width = Math.round(sizePx * dpr)
  canvas.height = Math.round(sizePx * dpr)
  canvas.style.width = `${sizePx}px`
  canvas.style.height = `${sizePx}px`
  canvas.style.display = 'block'
  if (label !== '') canvas.setAttribute('aria-label', label)
  const c = canvas.getContext('2d')
  if (c !== null) {
    c.scale(dpr, dpr)
    p(c, sizePx)
  }
  span.append(canvas)
  return span
}
